import { FC, useState } from "react";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import OverviewDragDropColumn from "./components/overviewDragDropColumn";
import { useOverviewContext } from "./context/overview.context";
import { APP_CONFIG } from "../../constants/constants";
import { TDashboardColumn } from "./types/overview.types";
import { useSelector } from "react-redux";
import { TModalsState } from "../../api/store/commonSlice";

// inspo from https://dev.to/imjoshellis/codealong-multi-column-drag-and-drop-in-react-3781
const OverviewDragDropContextArea: FC = () => {
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const {
    selectedDashboard,
    dashboardColumns,
    setDashboardColumns,
    selectedGroups,
    setSelectedGroups,
    customList,
    allTimeFilters,
  } = useOverviewContext();

  const onDragEnd = ({ source, destination }: DropResult) => {
    // Make sure we have a valid destination
    if (destination === undefined || destination === null) return null;

    // Make sure we're actually moving the item
    if (
      source.droppableId === destination.droppableId &&
      destination.index === source.index
    )
      return null;

    if (APP_CONFIG.DEBUG.ALL) console.log("source:", source); // {index: 0, droppableId: 'doing'}
    if (APP_CONFIG.DEBUG.ALL) console.log("destination:", destination); // {droppableId: 'todo', index: 2}

    // Set start and end columns
    const start =
      dashboardColumns[source.droppableId as keyof typeof dashboardColumns];
    const end =
      dashboardColumns[
        destination.droppableId as keyof typeof dashboardColumns
      ];
    if (APP_CONFIG.DEBUG.ALL) console.log("== start", start); // {id: 'doing', list: Array(1)}
    if (APP_CONFIG.DEBUG.ALL) console.log("== end", end); // {id: 'todo', list: Array(2)}

    // // If start is the same as end, we're in the same column
    if (start === end) {
      // Move the item within the list
      // Start by making a new list without the dragged item
      const newList =
        start?.colWidgets?.filter(
          (_: any, idx: number) => idx !== source.index
        ) ?? [];
      if (APP_CONFIG.DEBUG.ALL) console.log("newList", newList);

      // Then insert the item at the right location
      if (!!start?.colWidgets?.[source.index])
        newList?.splice(
          destination.index,
          0,
          start?.colWidgets?.[source.index]
        );

      // Then create a new copy of the column object
      const newCol = {
        id: start?.id,
        colWidgets: newList,
      } as TDashboardColumn;
      if (APP_CONFIG.DEBUG.ALL) console.log("newCol", newCol);

      // Update the state
      setDashboardColumns((state: typeof dashboardColumns) => ({
        ...state,
        [source.droppableId]: newCol, // [newCol.id]: newCol
      }));
      return null;
    } else {
      // If start is different from end, we need to update multiple columns
      // Filter the start list like before
      const newStartList = start.colWidgets.filter(
        (_: any, idx: number) => idx !== source.index
      );

      // Create a new start column
      const newStartCol = {
        id: start.id,
        colWidgets: newStartList,
      } as TDashboardColumn;

      // Make a new end list array
      const newEndList = end.colWidgets;

      // Insert the item into the end list
      newEndList.splice(destination.index, 0, start.colWidgets[source.index]);

      // Create a new end column
      const newEndCol = {
        id: end.id,
        colWidgets: newEndList,
      } as TDashboardColumn;

      // Update the state
      setDashboardColumns((state: typeof dashboardColumns) => ({
        ...state,
        [source.droppableId]: newStartCol, // [newStartCol.id]: newStartCol,
        [destination.droppableId]: newEndCol, // [newEndCol.id]: newEndCol
      }));
      return null;
    }
  };

  return (
    <>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <>
          <pre className="absolute bottom-10 right-60 text-[8px] h-48 overflow-auto z-10">
            allTimeFilters:
            <br />
            {JSON.stringify(allTimeFilters, null, 2)}
          </pre>
          <pre className="absolute bottom-10 right-0 text-[8px] h-48 overflow-auto z-10">
            selectedDashboard: {JSON.stringify(selectedDashboard, null, 2)}
            <br />
            dashboardColumns:
            <br />
            {JSON.stringify(dashboardColumns, null, 2)}
            <br />
            selectedGroups({selectedGroups.length}):
            <br />
            {JSON.stringify(selectedGroups, null, 2)}
            <br />
          </pre>
        </>
      )}
      <DragDropContext
        onDragEnd={onDragEnd}
        dragHandleUsageInstructions="Use the handle to drag"
      >
        <div
          className={`grid grid-cols-3 w-full min-h-[calc(100vh-220px)] gap-4${modalsState.showDashboardAddWidget ? " pe-[264px]" : ""}`}
          key={JSON.stringify(customList)} // refreshes widgets if custom widgets are edited
        >
          {Object.keys(dashboardColumns).map((key, i) => (
            <OverviewDragDropColumn
              columnData={dashboardColumns[key]}
              droppableId={key}
              key={`${dashboardColumns[key].id}__${i}`}
            />
          ))}
        </div>
      </DragDropContext>
    </>
  );
};
export default OverviewDragDropContextArea;
